import type { ProductPromptType } from '@/components/products/ProductsPromptModal/redux/types';

import type { CompletionVariant, UtmParams } from './types';

/**
 * Which illustration heads the Completion Step.
 *
 * The design (Figma 8705-8520) puts a full-bleed banner above the confirmation, and the artwork is
 * expected to change with the campaign a visitor arrived on — a partner's own imagery, a seasonal
 * push. Which conditions pick which image has not been agreed yet, so there is exactly one rule
 * today: everybody gets the default. The shape is here so that agreeing the second one is adding a
 * row to `HEADER_RULES`, not restructuring the step.
 *
 * This is *copy selection*, not product selection, so reading `utm` here is consistent with ADR
 * 0004: the campaign parameters may choose what a confirmation looks like — they already choose the
 * `partner-welcome` wording — and may never choose what is on sale.
 */

export interface CompletionHeaderInputs {
  /** The campaign parameters the flow snapshotted when it opened. */
  utm?: UtmParams;
  /** The offer the visitor was shown, from the URL or from the gate. */
  ppt?: ProductPromptType;
  /** Which confirmation is being shown — a partner welcome may want the partner's artwork. */
  variant: CompletionVariant;
}

export interface CompletionHeaderImage {
  src: string;
  /**
   * Empty on purpose for the default: it is a decorative banner sitting directly above a heading
   * that already says what happened, so announcing it would only repeat the card. A campaign image
   * that carries meaning of its own — a partner's logo, say — should describe itself here.
   */
  alt: string;
}

/**
 * Served from the app's own asset host rather than bundled. The set is expected to grow with
 * campaigns that marketing runs on their own schedule, and uploading a file should not need a
 * deploy. `welcome-default.webp` is the Figma banner exported at 2x (812×312 for a 406×156 slot).
 */
const ASSET_BASE = 'https://app.zoolife.tv/assets/onboarding';

export const DEFAULT_COMPLETION_HEADER: CompletionHeaderImage = {
  src: `${ASSET_BASE}/welcome-default.webp`,
  alt: '',
};

interface CompletionHeaderRule {
  /** What has to be true of the arrival for this image to be used. */
  when: (inputs: CompletionHeaderInputs) => boolean;
  image: CompletionHeaderImage;
}

/**
 * Checked in order; the first match wins. Empty until the conditions are confirmed — see the module
 * comment. A rule looks like:
 *
 * ```ts
 * { when: ({ utm }) => utm?.source === 'torontozoo', image: { src: `${ASSET_BASE}/welcome-tz.webp`, alt: '' } }
 * ```
 */
const HEADER_RULES: CompletionHeaderRule[] = [];

const resolveCompletionHeader = (inputs: CompletionHeaderInputs): CompletionHeaderImage =>
  HEADER_RULES.find((rule) => rule.when(inputs))?.image ?? DEFAULT_COMPLETION_HEADER;

export default resolveCompletionHeader;
